import { useCallback, useEffect, useMemo, useState } from 'react';

import InputSearch from '@cobliteam/ui/form/InputSearch';

import type { Vehicle } from '@cobliteam/common-types';

import { isVehicleDashcam, isVehicleWithOverSpeedingAlarm } from '@cobliteam/api-lib';

import { useVehicle } from '@cobliteam/hooks';

import DropdownVehiclesPlaceholder from './components/DropdownVehiclesPlaceholder';
import VehicleItem from './components/VehicleItem/VehicleItem';
import VehicleTitle from './components/VehicleTitle/VehicleTitle';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Sort from '@cobliteam/ui/components/Sort';

export enum VehicleOption {
  BUZZER = 'BUZZER',
}

export enum SortOption {
  ALPHABETICAL = 'ALPHABETICAL',
  ASSOCIATED = 'ASSOCIATED',
  DISSOCIATED = 'DISSOCIATED',
}

export interface MultiSelectVehiclesProps {
  size?: 'condensed' | 'regular';
  title?: string;
  selectedVehicles: Vehicle[];
  isOpen?: boolean;
  vehicleOption?: VehicleOption;
  disabledTooltip?: React.ReactNode | string;
  disabled?: boolean;
  disabledRule?: (vehicle: Vehicle) => boolean;
  filterRule?: (vehicle: Vehicle) => boolean;
  onChange: (vehicles: Vehicle[]) => void;
  onClear?: () => void;
}

const MultiSelectVehicles = ({
  size = 'regular',
  title,
  isOpen,
  selectedVehicles,
  vehicleOption,
  disabledTooltip,
  disabled,
  disabledRule,
  filterRule,
  onChange,
  onClear,
}: MultiSelectVehiclesProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedVehicles || []);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [vehicleOrdered, setVehicleOrdered] = useState<Vehicle[] | undefined>(undefined);
  const [sortOption, setSortOption] = useState<SortOption>(SortOption.ALPHABETICAL);

  const { data, isFetching } = useVehicle().getVehicles({});
  const vehicles = useMemo(() => {
    if (filterRule) {
      return data?.filter(filterRule);
    } else {
      return data;
    }
  }, [data, filterRule]);

  const onClick = useCallback(() => {
    setDraft(selectedVehicles || []);
  }, [selectedVehicles]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft(selectedVehicles || []);
    setSearch(undefined);
  }, [selectedVehicles]);

  const handleClear = useCallback(() => {
    onClear?.();
    setDraft([]);
  }, [onClear]);

  const toggleItem = useCallback(
    (dataItem: Vehicle) => {
      const newItemDraft = Array.from(draft);
      const existItem = newItemDraft.some((draftItem) => draftItem.id === dataItem.id);
      if (existItem) newItemDraft.splice(newItemDraft.map((v) => v.id).indexOf(dataItem.id), 1);
      if (!existItem) newItemDraft.push(dataItem);
      setDraft(newItemDraft);
    },
    [draft, setDraft]
  );

  const sortBySelected = useCallback(
    (a: Vehicle, b: Vehicle) => {
      const aIsSelected = selectedVehicles?.some((v) => v.id === a.id);
      const bIsSelected = selectedVehicles?.some((v) => v.id === b.id);
      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;
      return 0;
    },
    [selectedVehicles]
  );

  const sortByLabel = useCallback((a: Vehicle, b: Vehicle) => {
    const aLabel = a.label.toLowerCase() || '';
    const bLabel = b.label.toLowerCase() || '';
    if (aLabel < bLabel) return -1;
    if (aLabel > bLabel) return 1;
    return 0;
  }, []);

  const sortByDeviceAssociation = (option: SortOption) => (a: Vehicle, b: Vehicle) => {
    const aIsAssociated = !!a.deviceId;
    const bIsAssociated = !!b.deviceId;
    const isAssociated = option === SortOption.ASSOCIATED;
    if (isAssociated) return Number(bIsAssociated) - Number(aIsAssociated);
    return Number(aIsAssociated) - Number(bIsAssociated);
  };

  const getSortMethod = useCallback(
    (option: SortOption) => {
      const associationOptions = [SortOption.ASSOCIATED, SortOption.DISSOCIATED];
      if (associationOptions.includes(option)) {
        return sortByDeviceAssociation(option);
      }

      return sortByLabel;
    },
    [sortByLabel]
  );

  const filterOptions = (vehicle: Vehicle) => {
    if (vehicleOption === VehicleOption.BUZZER) {
      return vehicle.deviceId !== undefined && (isVehicleDashcam(vehicle) || isVehicleWithOverSpeedingAlarm(vehicle));
    }
    return true;
  };

  const filterBySearch = useCallback((vehicle: Vehicle) => (search ? vehicle.label.toLowerCase().includes(search.toLowerCase()) : true), [search]);

  // disable apply button if selected vehicles are the same as the draft
  const hasSameElements = selectedVehicles?.length === draft?.length && Array.from(selectedVehicles).every((value) => draft?.some((draftItem) => draftItem.id === value.id));

  const handleApply = useCallback(() => {
    handleClose();
    onChange(draft);
    if (vehicles) {
      const sortMethod = getSortMethod(sortOption);
      setVehicleOrdered(vehicles.sort(sortMethod).sort(sortBySelected));
    }
  }, [draft, getSortMethod, handleClose, onChange, sortBySelected, sortOption, vehicles]);

  const handleSortChange = (option: SortOption) => {
    if (vehicles) {
      setSortOption(option);
      const sortMethod = getSortMethod(option);
      setVehicleOrdered(vehicles.sort(sortMethod).sort(sortBySelected));
    }
  };

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  useEffect(() => {
    if (vehicles) {
      setVehicleOrdered(vehicles.sort(sortByLabel).sort(sortBySelected));
    }
  }, [vehicles]);

  const sortOptions = [
    { value: SortOption.ALPHABETICAL, key: 'Ordenar de A-Z' },
    { value: SortOption.ASSOCIATED, key: 'Ordenar por associados' },
    { value: SortOption.DISSOCIATED, key: 'Ordenar por desassociados' },
  ];

  const dataFiltered = vehicleOrdered?.filter(filterOptions)?.filter(filterBySearch).sort(sortBySelected);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='flex items-center justify-between px-2'>
            <Button type='button' variant='text' size='condensed' disabled={draft?.length === 0} onClick={handleClear}>
              Limpar seleção
            </Button>
            <Sort
              onChange={(option) => handleSortChange(option.value)}
              options={sortOptions}
              placement='right-start'
              defaultOption={sortOptions.find((option) => sortOption === option.value)}
            />
          </Dropdown.Header>
          <Dropdown.Search className='px-5'>
            <InputSearch placeholder='Procure por placa ou modelo' value={search} autoFocus debounceInMillis={500} onChange={setSearch} className='w-full' />
          </Dropdown.Search>
          <Dropdown.Body className='pl-5'>
            <Dropdown.List
              heightList={300}
              heightItem={32}
              data={dataFiltered || []}
              isLoading={isFetching}
              loadingComponent={<Dropdown.Loading />}
              placeholderComponent={<DropdownVehiclesPlaceholder />}
              itemComponent={(item: Vehicle) => {
                return (
                  <VehicleItem
                    data={item}
                    onChange={() => toggleItem(item)}
                    checked={draft.some((draftItem) => draftItem.id === item.id)}
                    disabled={disabledRule?.(item)}
                    disabledTooltip={disabledTooltip}
                  />
                );
              }}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button type='button' variant='text' full size='condensed' onClick={handleClose}>
                Cancelar
              </Button>
              <Button type='button' size='condensed' full onClick={handleApply} disabled={hasSameElements}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <VehicleTitle disabled={disabled} size={size} selectedVehicles={selectedVehicles} title={title} />
    </Dropdown>
  );
};

export default MultiSelectVehicles;
